import React, { useEffect, useMemo, useState } from "react";
import { Modal, Button, Form, Alert, Spinner } from "react-bootstrap";
import EnrollmentService from "../../../_services/EnrollmentService";

interface ApplyDiscountModalProps {
  show: boolean;
  onHide: () => void;
  enrollmentId: number;
  onSuccess: () => void;
}

interface ContextHead {
  headId: number;
  headName: string;
  outstanding: number;
  /** null = authority not enforced for this tenant */
  allowance: number | null;
}

interface ContextCategory {
  categoryId: number;
  name: string;
  typeId: number | null;
  value: number;
  lines: { headId: number; headName: string; amount: number }[];
  alreadyApplied: boolean;
  requiresApproval: boolean;
  approverRoleNames: string[];
}

interface PendingApproval {
  id: number;
  discountCategoryId: number | null;
  installmentHeadId: number | null;
}

const LUMPSUM = "lumpsum";

const fmt = (n: number) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

const extractError = (err: any, fallback: string) =>
  err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || fallback;

const pill = (bg: string, color: string): React.CSSProperties => ({
  display: "inline-block",
  padding: "1px 8px",
  borderRadius: 999,
  fontSize: 11,
  fontWeight: 600,
  background: bg,
  color,
  whiteSpace: "nowrap",
});

const ApplyDiscountModal: React.FC<ApplyDiscountModalProps> = ({
  show,
  onHide,
  enrollmentId,
  onSuccess,
}) => {
  const [heads, setHeads] = useState<ContextHead[]>([]);
  const [categories, setCategories] = useState<ContextCategory[]>([]);
  const [pendingApprovals, setPendingApprovals] = useState<PendingApproval[]>([]);
  const [authorityEnforced, setAuthorityEnforced] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [approvalMsg, setApprovalMsg] = useState<string | null>(null);

  const [discountType, setDiscountType] = useState<string>(LUMPSUM);
  const [installmentHeadId, setInstallmentHeadId] = useState<number | null>(null);
  const [amount, setAmount] = useState<string>("");
  const [amountMode, setAmountMode] = useState<"absolute" | "percent">("absolute");
  const [remarks, setRemarks] = useState("");

  const isLumpSum = discountType === LUMPSUM;

  useEffect(() => {
    if (show) {
      setError(null);
      setSuccessMsg(null);
      setApprovalMsg(null);
      setDiscountType(LUMPSUM);
      setInstallmentHeadId(null);
      setAmount("");
      setAmountMode("absolute");
      setRemarks("");
      setLoading(true);
      EnrollmentService.getDiscountContext(enrollmentId)
        .then((res: any) => {
          const d = res.data?.data || {};
          setHeads(d.heads || []);
          setCategories(d.categories || []);
          setPendingApprovals(d.pendingApprovals || []);
          setAuthorityEnforced(!!d.authority?.enforced);
        })
        .catch((err: any) => setError(extractError(err, "Failed to load discount options")))
        .finally(() => setLoading(false));
    }
  }, [show, enrollmentId]);

  const selectedHead = heads.find((h) => h.headId === installmentHeadId);
  const selectedCategory = categories.find((c) => String(c.categoryId) === discountType);

  const pendingForCategory = (categoryId: number) =>
    pendingApprovals.some((p) => p.discountCategoryId === categoryId);
  const pendingForHead = (headId: number | null) =>
    headId != null && pendingApprovals.some((p) => p.installmentHeadId === headId);

  // Live rupee estimate for the lump-sum inputs
  const lumpSumEstimate = useMemo(() => {
    const n = Number(amount);
    if (!selectedHead || !n || n <= 0) return null;
    return amountMode === "percent"
      ? Math.round(((selectedHead.outstanding * n) / 100) * 100) / 100
      : n;
  }, [amount, amountMode, selectedHead]);

  const overAllowance =
    isLumpSum &&
    authorityEnforced &&
    selectedHead?.allowance != null &&
    lumpSumEstimate != null &&
    lumpSumEstimate > selectedHead.allowance;

  // Whether the current selection will need an approval step
  const needsApproval = isLumpSum ? overAllowance : !!selectedCategory?.requiresApproval;

  const handleSubmit = async (viaApproval: boolean) => {
    setError(null);

    const body: any = { remarks: remarks.trim() || "Discount applied" };
    if (viaApproval) body.sendForApproval = true;

    if (isLumpSum) {
      if (!installmentHeadId) return setError("Select a fee head");
      const numAmount = Number(amount);
      if (!numAmount || numAmount <= 0) return setError("Amount must be > 0");
      if (amountMode === "percent" && numAmount > 100) return setError("Percentage cannot exceed 100%");
      if (selectedHead && lumpSumEstimate != null && lumpSumEstimate > selectedHead.outstanding) {
        return setError(`Amount exceeds outstanding (${fmt(selectedHead.outstanding)})`);
      }
      body.installmentHeadId = installmentHeadId;
      body.amount = numAmount;
      body.amountMode = amountMode;
    } else {
      if (!selectedCategory) return setError("Select a discount");
      body.discountCategoryId = selectedCategory.categoryId;
    }

    setSubmitting(true);
    try {
      const res = await EnrollmentService.applyDiscount(enrollmentId, body);
      const data = res.data?.data;
      if (data?.approvalRequested) {
        setApprovalMsg(
          isLumpSum
            ? `Approval request sent for ${selectedHead?.headName}.`
            : `Approval request sent for "${selectedCategory?.name}". Approvers: ${selectedCategory?.approverRoleNames.join(", ")}.`,
        );
        onSuccess();
      } else if (res.data?.success) {
        const applied = data?.discountApplied;
        setSuccessMsg(
          isLumpSum
            ? `Discount of ${fmt(applied)} applied to ${selectedHead?.headName}`
            : `"${selectedCategory?.name}" applied — student saves ${fmt(applied)}`,
        );
        onSuccess();
      } else {
        setError(res.data?.message || "Failed to apply discount");
      }
    } catch (err: any) {
      const code = err?.response?.data?.error?.code;
      if (code === "DISCOUNT_APPROVAL_REQUIRED") {
        // Fallback path (context was stale) — offer the approval route inline.
        setError(extractError(err, "This discount needs approval."));
      } else {
        setError(extractError(err, "Failed to apply discount"));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const done = !!(successMsg || approvalMsg);

  return (
    <Modal show={show} onHide={onHide} centered contentClassName="ev-modal">
      <Modal.Header closeButton>
        <Modal.Title>Apply Discount</Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ fontSize: 13 }}>
        {error && <Alert variant="danger" style={{ fontSize: 12 }}>{error}</Alert>}
        {successMsg && <Alert variant="success" style={{ fontSize: 12 }}>{successMsg}</Alert>}
        {approvalMsg && <Alert variant="info" style={{ fontSize: 12 }}>{approvalMsg}</Alert>}

        {loading ? (
          <div style={{ textAlign: "center", padding: 20 }}>
            <Spinner role="status" animation="border" size="sm" /> Loading...
          </div>
        ) : !done && (
          <>
            <Form.Group className="mb-2">
              <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Discount</Form.Label>
              <Form.Select
                size="sm"
                value={discountType}
                onChange={(e) => { setDiscountType(e.target.value); setError(null); }}
              >
                <option value={LUMPSUM}>Ad-hoc (lump sum)</option>
                {categories.map((c) => (
                  <option
                    key={c.categoryId}
                    value={String(c.categoryId)}
                    disabled={c.alreadyApplied || c.value <= 0 || pendingForCategory(c.categoryId)}
                  >
                    {c.name}
                    {c.value > 0 ? ` — saves ${fmt(c.value)}` : " — nothing outstanding"}
                    {c.alreadyApplied ? " (already availed)" : ""}
                    {pendingForCategory(c.categoryId) ? " (approval pending)" : ""}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Structured discount: show exactly what it grants + approval status */}
            {selectedCategory && (
              <div
                style={{
                  border: "1px solid var(--border-default, #e3e1ee)",
                  borderRadius: 10,
                  padding: "10px 12px",
                  marginBottom: 10,
                  background: "var(--surface-sunken, #faf9ff)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                  <strong style={{ fontSize: 12.5 }}>What the student gets</strong>
                  <span style={pill("#ede9fe", "#6d28d9")}>saves {fmt(selectedCategory.value)}</span>
                </div>
                {selectedCategory.lines.map((l) => (
                  <div key={l.headId} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "2px 0" }}>
                    <span style={{ color: "var(--text-muted, #6b6880)" }}>{l.headName}</span>
                    <span>− {fmt(l.amount)}</span>
                  </div>
                ))}
                {selectedCategory.requiresApproval && (
                  <div style={{ marginTop: 8 }}>
                    <span style={pill("#fef3c7", "#92400e")}>
                      Needs approval — {selectedCategory.approverRoleNames.join(", ")}
                    </span>
                  </div>
                )}
              </div>
            )}

            {isLumpSum && (
              <>
                <Form.Group className="mb-2">
                  <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Head</Form.Label>
                  <Form.Select
                    size="sm"
                    value={installmentHeadId || ""}
                    onChange={(e) => { setInstallmentHeadId(Number(e.target.value) || null); setError(null); }}
                  >
                    <option value="">-- Select Head --</option>
                    {heads.map((h) => (
                      <option key={h.headId} value={h.headId}>
                        {h.headName} (Outstanding: {fmt(h.outstanding)})
                      </option>
                    ))}
                  </Form.Select>
                  {selectedHead && authorityEnforced && selectedHead.allowance != null && (
                    <div style={{ marginTop: 5 }}>
                      <span style={selectedHead.allowance > 0 ? pill("#dcfce7", "#166534") : pill("#fef3c7", "#92400e")}>
                        Your authority: {selectedHead.allowance > 0 ? `up to ${fmt(selectedHead.allowance)}` : "none — approval needed"}
                      </span>
                      {pendingForHead(installmentHeadId) && (
                        <span style={{ ...pill("#dbeafe", "#1e40af"), marginLeft: 6 }}>Approval already pending</span>
                      )}
                    </div>
                  )}
                </Form.Group>

                <Form.Group className="mb-2">
                  <div style={{ display: "flex", gap: 8 }}>
                    <div style={{ flex: 1 }}>
                      <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Amount</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        min={0}
                        value={amount}
                        onChange={(e) => { setAmount(e.target.value); setError(null); }}
                      />
                    </div>
                    <div style={{ width: 90 }}>
                      <Form.Label style={{ fontSize: 12, fontWeight: 600 }}>Type</Form.Label>
                      <Form.Select
                        size="sm"
                        value={amountMode}
                        onChange={(e) => setAmountMode(e.target.value as any)}
                      >
                        <option value="absolute">Flat</option>
                        <option value="percent">%</option>
                      </Form.Select>
                    </div>
                  </div>
                  {lumpSumEstimate != null && amountMode === "percent" && selectedHead && (
                    <div style={{ fontSize: 11.5, color: "var(--text-muted, #6b6880)", marginTop: 4 }}>
                      = {fmt(lumpSumEstimate)} off {selectedHead.headName}
                    </div>
                  )}
                  {overAllowance && (
                    <div style={{ marginTop: 6 }}>
                      <span style={pill("#fef3c7", "#92400e")}>
                        Exceeds your authority ({fmt(selectedHead!.allowance!)}) — this will be sent for approval
                      </span>
                    </div>
                  )}
                </Form.Group>
              </>
            )}

            <Form.Group className="mb-2">
              <Form.Label style={{ fontSize: 12 }}>Remarks</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                size="sm"
                value={remarks}
                onChange={(e) => setRemarks(e.target.value)}
                placeholder="Reason for discount..."
              />
            </Form.Group>
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        {done ? (
          <Button variant="secondary" size="sm" onClick={onHide}>Close</Button>
        ) : (
          <>
            <Button variant="secondary" size="sm" onClick={onHide}>Cancel</Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => handleSubmit(!!needsApproval)}
              disabled={submitting || loading}
            >
              {submitting ? (
                <>
                  <Spinner role="status" animation="border" size="sm" />{" "}
                  {needsApproval ? "Sending..." : "Applying..."}
                </>
              ) : needsApproval ? (
                "Send for Approval"
              ) : (
                "Apply Discount"
              )}
            </Button>
          </>
        )}
      </Modal.Footer>
    </Modal>
  );
};

export default ApplyDiscountModal;
